import React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { calculateTicketTotalsWithFee } from '../../../utils/money';
import type { TicketCartItem } from '../../../utils/ticketCart';
import { cartPersonCount } from '../../../utils/ticketCart';

type Props = {
  open: boolean;
  onClose: () => void;
  items: TicketCartItem[];
  feePercentage?: number;
  onChangeQuantity: (ticketId: string, quantity: number) => void;
  onRemove: (ticketId: string) => void;
  onContinue: () => void;
};

export const TicketCartBadgeButton: React.FC<{
  count: number;
  onClick: () => void;
  color?: string;
}> = ({ count, onClick, color }) => (
  <IconButton onClick={onClick} aria-label="Abrir carrinho" sx={{ color: color || 'inherit' }}>
    <Badge badgeContent={count} color="primary">
      <ShoppingCartIcon />
    </Badge>
  </IconButton>
);

const TicketCartDrawer: React.FC<Props> = ({
  open,
  onClose,
  items,
  feePercentage,
  onChangeQuantity,
  onRemove,
  onContinue,
}) => {
  const people = cartPersonCount(items);
  const total = items.reduce((sum, item) => {
    const line = calculateTicketTotalsWithFee({
      unitPrice: item.price,
      quantity: item.quantity,
      feePercentage,
      showPlatformFee: item.showPlatformFee,
    });
    return sum + line.total;
  }, 0);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 380 }, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Carrinho
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {people} {people === 1 ? 'pessoa' : 'pessoas'} neste pedido
        </Typography>

        {items.length === 0 ? (
          <Typography color="text.secondary">Seu carrinho está vazio.</Typography>
        ) : (
          items.map((item) => {
            const line = calculateTicketTotalsWithFee({
              unitPrice: item.price,
              quantity: item.quantity,
              feePercentage,
              showPlatformFee: item.showPlatformFee,
            });
            return (
              <Box key={item.ticketId} sx={{ mb: 2 }}>
                <Typography fontWeight={600}>{item.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.price > 0 ? `R$ ${line.total.toFixed(2)}` : 'Gratuito'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => onChangeQuantity(item.ticketId, item.quantity - 1)}
                    aria-label="Diminuir quantidade"
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography>{item.quantity}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => onChangeQuantity(item.ticketId, item.quantity + 1)}
                    aria-label="Aumentar quantidade"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(item.ticketId)}
                    aria-label="Remover ingresso"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}

        <Box sx={{ mt: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Total: {total > 0 ? `R$ ${total.toFixed(2)}` : 'Gratuito'}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            disabled={people < 1}
            onClick={onContinue}
          >
            Continuar
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TicketCartDrawer;
